/**
 * state.ts - Game State Definition
 */

import type { RoomTypeId } from './data/rooms.js';
import type { GhostId } from './data/ghosts.js';
import type { VisitorTypeId } from './data/visitors.js';
import type { TrainingId } from './data/trainings.js';
import type { PropId } from './data/props.js';
import type { FacilityId } from './data/facilities.js';
import type { AcademyTrainingId } from './data/academyTrainings.js';
import type { LobbyPropId } from './data/lobbyProps.js';
import type { FacilityPropId } from './data/facilityProps.js';
import type { LobbyFacilityType } from './data/lobbyFacilities.js';
// NOTE: LobbyRoomType kept for backward compat but LobbyFacilityType is the unified type
import type { LobbyRoomType } from './data/lobbyRooms.js';

// ── Lobby NPC Ghost ────────────────────────────────────────

export interface LobbyNpcGhost {
  ghostId: string;          // ghost instance ID assigned to lobby scare duty
  assignedAt: number;       // timestamp ms when assigned
  lastScareAt: number;      // timestamp ms of last scare action
}

// ── Random Event System ────────────────────────────────────

export interface ActiveEvent {
  eventId: string;
  startedAt: number;
  expiresAt: number;   // 0 = wait for player choice
  defended: boolean;    // true if auto-defended by staff/facilities
}


// ── View System ────────────────────────────────────────────

export type MainView = 'building' | 'lobby' | 'facilities' | 'academy';

// ── Day Phase Cycle ────────────────────────────────────────

export type DayPhase = 'day' | 'evening' | 'night';

// ── Ghost Staff ────────────────────────────────────────────

export type GhostStaffType = 'repairman' | 'janitor' | 'promoter' | 'medic' | 'security';

export interface GhostStaff {
  id: string;
  type: GhostStaffType;
  name: string;
  level: number;       // 1-5
  salary: number;      // daily SP cost
  assignedTo: string | null;  // floor level or facility ID
  hiredAt: number;     // day number
}

export const GHOST_STAFF_DEFS: Record<GhostStaffType, { hireCost: number; salary: number; image: string; nameKey: string; descKey: string; effectKey: string }> = {
  repairman:  { hireCost: 500, salary: 50, image: '/images/ghost-staff/repairman.webp', nameKey: 'gstaff_repairman',  descKey: 'gstaff_repairman_desc',  effectKey: 'gstaff_repairman_effect' },
  janitor:    { hireCost: 500, salary: 50, image: '/images/ghost-staff/janitor.webp',   nameKey: 'gstaff_janitor',    descKey: 'gstaff_janitor_desc',    effectKey: 'gstaff_janitor_effect' },
  promoter:   { hireCost: 500, salary: 50, image: '/images/ghost-staff/promoter.webp',  nameKey: 'gstaff_promoter',   descKey: 'gstaff_promoter_desc',   effectKey: 'gstaff_promoter_effect' },
  medic:      { hireCost: 500, salary: 50, image: '/images/ghost-staff/medic.webp',     nameKey: 'gstaff_medic',      descKey: 'gstaff_medic_desc',      effectKey: 'gstaff_medic_effect' },
  security:   { hireCost: 500, salary: 50, image: '/images/ghost-staff/security.webp',  nameKey: 'gstaff_security',   descKey: 'gstaff_security_desc',   effectKey: 'gstaff_security_effect' },
};

// ── Building ───────────────────────────────────────────────

export interface RoomProp {
  propId: PropId;
  level: number;  // 1-3
}

export interface Room {
  id: string;
  type: RoomTypeId;
  upgradeLevel: 1 | 2 | 3;
  assignedGhostId: string | null;
  props: RoomProp[];
  condition: number;          // 0-100, facility deterioration (100=perfect, 0=ruins)
  hygiene: number;            // 0-100, cleanliness (100=clean, 0=filthy)
  conditionUpgrade: number;   // 0-20, structural reinforcement level
  hygieneUpgrade: number;     // 0-20, auto-purification level
  collapsed: boolean;         // true if condition hit 0
  underRepair: boolean;       // true if repair crew dispatched
  underCleaning: boolean;     // true if cleaning crew dispatched
  repairEndsAt: number;       // timestamp ms when repair completes (0 = not repairing)
  cleaningEndsAt: number;     // timestamp ms when cleaning completes (0 = not cleaning)
}

/** A floor in the building. Each floor has a single room. */
export interface Floor {
  level: number;      // 1 = ground floor, 2 = second floor, etc.
  room: Room | null;  // null = empty (no room built yet)
}

export type HauntedThemeId =
  | 'haunted_house'
  | 'abandoned_hospital'
  | 'school_back_mountain'
  | 'cursed_circus'
  | 'ancient_temple'
  | 'pirate_ship'
  | 'abandoned_mine'
  | 'gothic_castle'
  | 'underground_bunker'
  | 'cursed_forest';
export type HauntedThemeSelection = HauntedThemeId | null;

export interface HauntedBuildingState {
  id: string;
  name: string;
  themeId: HauntedThemeSelection;
  maxFloors: number;
  buildingExteriorAsset: string;
  lobbyStyleId: string;
  passiveRevenueMultiplier: number;
  building: Floor[];
  visitorGroup: VisitorGroup | null;
  reviews: ReviewEntry[];
  isOpen: boolean;
  ticketPrice: number;
  dayNumber: number;
  dayStartTime: number;
  /** Tutorial step: 0=not started, 1=welcome, 2=build room, 3=assign ghost, 4=open house, 99=completed */
  tutorialStep: number;
  todayVisitors: number;
  todayEarnings: number;
  reputation: number;
  recommendationPool: number;
  todayFleeCount: number;
  lobbyQueue: LobbyVisitor[];
  activeTeams: VisitorTeam[];
  maxConcurrentTeams: number;
  lobbyStaff: string[];
  lobbyWaitQueue: QueueVisitor[];
  maxQueueSize: number;
  lobbyFacilities: LobbyFacilityInstance[];
  queueTickAccumMs: number;
  queueSpawnAccumMs: number;
  lobbyBuilding: LobbyFloor[];
  facilityBuilding: FacilityFloor[];
  facilityMap: FacilityPlot[];
  storyBonus: number;
  storyGrade: string;
  activeEvent: ActiveEvent | null;
  eventHistory: string[];
  eventCooldownUntil: number;
  eventTickAccumMs: number;
}

export interface HauntedBuildingMeta {
  id: string;
  name: string;
  themeId: HauntedThemeSelection;
  buildingExteriorAsset: string;
  lobbyStyleId: string;
  passiveRevenueMultiplier: number;
  maxFloors: number;
  isUnlocked: boolean;
}

export interface HauntedBuildingSummary {
  id: string;
  name: string;
  themeId: HauntedThemeSelection;
  buildingExteriorAsset: string;
  lobbyStyleId: string;
  passiveRevenueMultiplier: number;
  maxFloors: number;
  isUnlocked: boolean;
  floorsBuilt: number;
  todayEarnings: number;
  reputation: number;
  queueCount: number;
  isOpen: boolean;
}

export type WorldScreen = 'world' | 'haunted_building';

// ── Ghosts ─────────────────────────────────────────────────

export interface GhostTraining {
  trainingId: TrainingId;
  startedAt: number;       // timestamp ms
  completesAt: number;     // timestamp ms
}

/** Staff role for non-scare duties */
export type StaffRole = 'scare' | 'repair' | 'janitor' | 'ticket' | 'guide' | 'lobby_staff' | 'facility_staff' | 'instructor';

/** Ghost role assignment */
export type GhostRole = StaffRole;

/** Academy training session for a ghost */
export interface AcademySession {
  trainingId: AcademyTrainingId;
  startedAt: number;
  completesAt: number;
}

/** Academy skill levels per ghost */
export interface AcademySkills {
  acting: number;           // 0-5: Acting Class
  human_relations: number;  // 0-5: Human Relations
  psychology: number;       // 0-5: Psychology
  special_fx: number;       // 0-5: Special FX
  fitness: number;          // 0-5: Fitness
  teamwork: number;         // 0-5: Teamwork
  customer_service: number; // 0-5: Customer Service
}

export const INITIAL_ACADEMY_SKILLS: AcademySkills = {
  acting: 0,
  human_relations: 0,
  psychology: 0,
  special_fx: 0,
  fitness: 0,
  teamwork: 0,
  customer_service: 0,
};

/** Staff grade based on skills and performance */
export type StaffGrade = 'S' | 'A' | 'B' | 'C' | 'D' | 'F';

export interface GhostInstance {
  id: string;             // unique instance id (uuid-like)
  characterId: GhostId;
  level: number;          // 1-20
  experience: number;
  fatigue: number;        // 0-100
  assignedRoomId: string | null;
  training: GhostTraining | null;
  /** Accumulated scare bonus from completed trainings */
  trainingBonus: number;
  /** Number of completed trainings */
  trainingCount: number;
  /** Ghost role assignment */
  role: GhostRole;
  /** Facility ID if assigned as facility_staff */
  assignedFacilityId: FacilityId | null;
  /** Academy session if in academy training */
  academySession: AcademySession | null;
  /** Academy skill levels */
  academySkills: AcademySkills;
  /** Timestamp when this ghost was hired */
  hiredAt: number;
  /** Number of praise received from visitors */
  praiseCount: number;
}

// ── Lobby ──────────────────────────────────────────────────

export interface LobbyVisitor {
  id: string;
  type: VisitorTypeId;
  patience: number;        // patience remaining (decreases while waiting)
  enteredQueueAt: number;  // timestamp ms
}

export interface VisitorTeam {
  id: string;
  visitors: LobbyVisitor[];
  enteredBuildingAt: number;
}

// ── Queue Visitors (Waiting Room) ──────────────────────────

export interface QueueVisitor {
  id: string;
  type: VisitorTypeId;
  patience: number;        // 0-100, decreases over time
  maxPatience: number;     // maximum patience at arrival
  arrivalTime: number;     // timestamp ms when arrived in queue
  isVip: boolean;
}

// ── Lobby Facilities ───────────────────────────────────────

export interface LobbyFacilityInstance {
  id: string;
  type: LobbyFacilityType;
  level: number;  // 1-3
  assignedStaffId: string | null;  // ghost NPC assigned as staff
}

// ── Visitors ───────────────────────────────────────────────

export type VisitorReaction = 'scared' | 'bored' | 'normal' | 'angry' | 'excited' | null;

export interface Visitor {
  id: string;
  type: VisitorTypeId;
  fearLevel: number;        // 0-100
  // === Roller-coaster-tycoon style attributes ===
  fearTolerance: number;    // personal fear tolerance (randomized from type base)
  excitement: number;       // current excitement level
  nausea: number;           // current nausea level (too much fear)
  satisfaction: number;     // running satisfaction score (0-1)
  budget: number;           // SP budget for gift shop etc.
  patience: number;         // patience remaining (decreases on empty rooms)
  fled: boolean;            // true if visitor fled early
  /** Current reaction after visiting a room */
  currentReaction: VisitorReaction;
  /** Timestamp when reaction was set (for auto-fade) */
  reactionSetAt: number;
}

/** Phase of the visitor walk-through sequence at each floor */
export type VisitorPhase = 'entering' | 'fog' | 'scare' | 'leaving';

export interface VisitorGroup {
  id: string;
  visitors: Visitor[];
  currentFloorIndex: number;  // index into building.floors (0 = ground)
  currentRoomIndex: number;   // index within that floor's rooms
  /** ms elapsed at current room (waiting for scare) */
  timeAtRoomMs: number;
  scareChainCount: number;
  done: boolean;
  /** Track total SP earned by this group for review calculation */
  totalSpEarned: number;
  /** Track how many rooms with ghosts this group visited */
  roomsVisitedWithGhost: number;
  /** If true, this is a VIP group (from ad reward) */
  isVip: boolean;
  /** Current phase within the walk-through sequence */
  phase: VisitorPhase;
  /** Timestamp (ms) when the current phase started */
  phaseStartedAt: number;
}

// ── Notifications ──────────────────────────────────────────

export interface ScarePopup {
  id: string;
  ghostId: string;
  spEarned: number;
  floorLevel: number;
  expiresAt: number;      // timestamp
  skillActivated?: boolean;
}

// ── Reviews ────────────────────────────────────────────────

export interface ReviewEntry {
  id: string;
  stars: 1 | 2 | 3 | 4 | 5;
  textKey: string;
  ghostNameKey: string | null;
  timestamp: number;
  visitorType?: string;
  floorLevel?: number;
  satisfaction?: number;
}

// ── Daily Milestones ───────────────────────────────────────

export interface DailyMilestone {
  id: string;
  nameKey: string;   // i18n key
  target: number;
  current: number;
  completed: boolean;
  reward: number;     // SP reward
  collected: boolean; // true if reward has been collected
}

// ── Facilities ─────────────────────────────────────────────

export interface FacilityInstance {
  facilityId: FacilityId;
  level: number;  // 1-5
  /** Ghost ID assigned as staff to this facility */
  assignedStaffId: string | null;
}

// ── Lobby Building (Floor-based) ───────────────────────────

export interface LobbyProp {
  propId: LobbyPropId;
  level: number;  // 1-3
}

export interface LobbyFloor {
  level: number;
  /** Unified: facility type installed on this floor (null = empty floor) */
  facilityType: LobbyFacilityType | null;
  facilityLevel: number;     // facility upgrade level 1-3
  assignedGhostId: string | null;
  props: LobbyProp[];
  // Legacy fields kept for migration compat (not used in new code)
  type?: LobbyRoomType;
  upgradeLevel?: number;
}

// ── Facility Building (Floor-based) ────────────────────────

export interface FacilityProp {
  propId: FacilityPropId;
  level: number;  // 1-3
}

export interface FacilityFloor {
  level: number;
  type: FacilityId;
  upgradeLevel: number;
  assignedGhostId: string | null;
  props: FacilityProp[];
}

// ── Facility Map (Plot-based grid) ─────────────────────────

export interface FacilityPlotFacility {
  type: FacilityId;
  upgradeLevel: number;
  assignedGhostId: string | null;
  props: FacilityProp[];
  price: number;          // usage price (SP) visitors pay
  totalRevenue: number;   // accumulated revenue from this facility
  /** Whether this facility is open for visitors */
  isOpen: boolean;
  /** For haunted_house type: which building this plot links to */
  buildingId?: string;
  /** Visitor satisfaction tracking */
  totalRatings: number;
  ratingSum: number;
  visitorCount: number;
}

export interface FacilityPlot {
  id: number;           // 0-15 (16 plots total, 8 rows x 2 columns)
  unlocked: boolean;    // whether this plot is available
  facility: FacilityPlotFacility | null; // null = empty plot
}

// ── Ad Boosts ──────────────────────────────────────────────

export interface AdBoost {
  type: 'sp_double';
  expiresAt: number;  // timestamp ms
}

// ── Main State ─────────────────────────────────────────────

export interface GameStateData {
  sp: number;
  fame: number;
  prestigeCoins: number;
  prestigeLevel: number;
  totalSpEarned: number;

  selectedHauntedBuildingId: string;
  worldScreen: WorldScreen;
  hauntedBuildings: HauntedBuildingState[];
  hauntedBuildingMetas: HauntedBuildingMeta[];

  building: Floor[];
  ghosts: GhostInstance[];

  visitorGroup: VisitorGroup | null;
  nextVisitorSpawnAt: number;   // timestamp (ms)

  /** ms accumulated for fatigue recovery tick */
  fatigueTickAccumMs: number;

  scarePopups: ScarePopup[];
  lastSaveTime: number;

  /** Unlocked room types (by id) */
  unlockedRoomTypes: RoomTypeId[];

  /** SNS reviews from visitors */
  reviews: ReviewEntry[];

  /** Auxiliary facilities (gift shop, snack bar, photo booth) */
  facilities: FacilityInstance[];

  /** Active ad boosts */
  adBoosts: AdBoost[];

  // === Star Evolution System ===
  /** Star level (0-25): determines star display */
  starLevel: number;
  /** Star evolution tier (0=gray, 1=bronze, 2=silver, 3=gold, 4=rainbow) */
  starEvolution: number;

  // === Business / Open-Close System ===
  /** Whether the haunted house is open for business */
  isOpen: boolean;
  /** Ticket price (base SP earned modifier) */
  ticketPrice: number;
  /** Current day number (Day 1, Day 2...) */
  dayNumber: number;
  tutorialStep: number;
  /** Timestamp when today's business session started */
  dayStartTime: number;
  /** Number of visitors today */
  todayVisitors: number;
  /** Total SP earned today */
  todayEarnings: number;

  // === Roller-coaster-tycoon style systems ===
  /** Reputation score (affects visitor spawn rate & quality). 0-100 */
  reputation: number;
  /** Time phase: 'day' | 'night' | 'rain' — affects scare & spawn */
  timePhase: 'day' | 'night' | 'rain';
  /** Timestamp when current time phase started */
  timePhaseStartAt: number;
  /** Recommendation bonus: satisfied visitors bring friends (accumulated) */
  recommendationPool: number;
  /** Today's flee count (visitors who left early) */
  todayFleeCount: number;

  // === View System ===
  /** Currently active main view */
  currentView: MainView;

  // === Lobby System ===
  /** Visitors waiting in the lobby queue */
  lobbyQueue: LobbyVisitor[];
  /** Teams currently exploring the building */
  activeTeams: VisitorTeam[];
  /** Maximum concurrent teams in building */
  maxConcurrentTeams: number;
  /** Ghost IDs assigned as lobby staff */
  lobbyStaff: string[];

  // === Lobby Waiting Room System ===
  /** Visitors in the waiting queue (patience-based) */
  lobbyWaitQueue: QueueVisitor[];
  /** Maximum queue capacity (upgradeable) */
  maxQueueSize: number;
  /** Installed lobby facilities */
  lobbyFacilities: LobbyFacilityInstance[];
  /** Accumulated ms for queue tick processing */
  queueTickAccumMs: number;
  /** Accumulated ms for queue spawn processing */
  queueSpawnAccumMs: number;

  // === Lobby Building (Floor-based) ===
  /** Lobby floors, building-style view */
  lobbyBuilding: LobbyFloor[];

  // === Facility Building (Floor-based) ===
  /** Facility floors, building-style view */
  facilityBuilding: FacilityFloor[];

  // === Facility Map (Plot-based grid) ===
  /** Top-down grid map with 12 plots (3x4) for facilities */
  facilityMap: FacilityPlot[];

  // === Story Arc System ===
  /** Story arc bonus percentage (0-50) applied to SP earnings */
  storyBonus: number;
  /** Story arc grade (S/A/B/C/D/F) */
  storyGrade: string;

  // === Daily Milestones ===
  /** Today's milestone challenges */
  dailyMilestones: DailyMilestone[];

  // === Day Phase Cycle ===
  /** Current phase of the day: day (prep) / evening (business) / night (summary) */
  dayPhase: DayPhase;
  /** Timestamp when the current day phase started */
  dayPhaseStartedAt: number;
  /** Game speed multiplier for evening phase */
  gameSpeed: 1 | 2;

  // === Human Staff System ===
  /** Non-ghost human staff hired by the player */
  ghostStaff: GhostStaff[];
  /** Total salaries paid this day (for Day Summary) */
  todaySalaries: number;

  // === Lobby NPC Ghost System ===
  /** NPC ghosts assigned to scare visitors in the lobby queue */
  lobbyNpcGhosts: LobbyNpcGhost[];

  // === Random Event System ===
  /** Currently active random event (null if none) */
  activeEvent: ActiveEvent | null;
  /** Recent event IDs for dedup (max 10) */
  eventHistory: string[];
  /** Cooldown: next event cannot fire before this timestamp */
  eventCooldownUntil: number;
  /** Accumulated ms for event tick */
  eventTickAccumMs: number;

  // === Street Visitor System (RCT-style) ===
  /** Visitors walking around the facility street */
  streetVisitors: StreetVisitor[];
  /** Accumulated ms for street visitor spawn tick */
  streetSpawnAccumMs: number;
  /** Max concurrent street visitors */
  maxStreetVisitors: number;
  /** Accumulated ms for street AI tick */
  streetAiAccumMs: number;
  /** Word-of-mouth multiplier (affects spawn rate, driven by visitor satisfaction) */
  wordOfMouth: number;
  /** Current season event (null = no event) */
  seasonEvent: SeasonEvent | null;
  /** Timestamp when current season event started */
  seasonEventStartedAt: number;
  /** Flag set by ad_vip_visitor command to spawn VIP on next tick */
  pendingVipSpawn?: boolean;
}

// ── Season Events ──────────────────────────────────────────

export type SeasonEventId = 'halloween' | 'summer_fest' | 'winter_chill' | 'spring_bloom';

export interface SeasonEvent {
  id: SeasonEventId;
  spawnMultiplier: number;       // multiply visitor spawn rate
  needBoosts: Partial<StreetVisitorNeeds>;  // boost specific needs
  revenueMultiplier: number;     // multiply all revenue
  durationMs: number;            // how long the event lasts
}

// ── Street Visitor (RCT-style autonomous) ──────────────────

export type StreetVisitorType = 'thrill_seeker' | 'family' | 'couple' | 'vip';

export type VipRequirementType =
  | 'theme_match'        // Must visit a haunted house matching their preferred theme
  | 'staffed_only'       // Only use facilities with assigned staff
  | 'no_wait'            // Leave immediately if any facility is full
  | 'premium_tour'       // Only visit haunted houses with 3+ active rooms
  | 'full_experience';   // Must visit at least 3 different facility types

export interface VipRequirement {
  type: VipRequirementType;
  met: boolean;
}

export type StreetVisitorNeed = 'fear_thrill' | 'hunger' | 'bathroom' | 'souvenir' | 'rest';

export type StreetVisitorState =
  | 'walking'       // Moving to a destination
  | 'using'         // Inside a facility
  | 'leaving';      // Headed to exit

export interface StreetVisitorNeeds {
  fear_thrill: number;  // 0-100: desire to visit haunted house
  hunger: number;       // 0-100: want snack bar
  bathroom: number;     // 0-100: need restroom
  souvenir: number;     // 0-100: want gift shop
  rest: number;         // 0-100: want to sit/rest (parking/first aid)
}

export interface StreetVisitor {
  id: string;
  /** Visitor type — determines needs profile and spending behavior */
  visitorType: StreetVisitorType;
  /** Avatar index (0-7) for visual variety */
  avatarIdx: number;
  /** Current position on the street (0.0 = entrance, 1.0 = end) */
  posY: number;
  /** Target plot ID (-1 = no target, -2 = exit) */
  targetPlotId: number;
  /** Current state */
  state: StreetVisitorState;
  /** Need levels (0-100 each) */
  needs: StreetVisitorNeeds;
  /** SP this visitor has spent (total revenue contribution) */
  totalSpent: number;
  /** How long this visitor has been on the street (ms) */
  timeOnStreetMs: number;
  /** Max time before they leave (ms) */
  patienceMs: number;
  /** If using a facility, remaining ms */
  useTimeRemainingMs: number;
  /** Satisfaction level 0-100 (affects review/recommendation) */
  satisfaction: number;
  /** Preferred haunted building theme (visitors gravitate toward matching buildings) */
  preferredTheme?: string;
  /** VIP-specific special requirements (only for vip type) */
  vipRequirements?: VipRequirement[];
  /** Count of distinct facility types used (for full_experience requirement) */
  usedFacilityTypes?: string[];
}

export function createInitialHauntedBuildingState(): HauntedBuildingState {
  return {
    id: 'building_1',
    name: '나의 첫 번째 귀신의 집',
    themeId: 'haunted_house',
    maxFloors: 5,
    buildingExteriorAsset: '/images/views/building-exterior-haunted-house.webp',
    lobbyStyleId: 'classic_haunted_lobby',
    passiveRevenueMultiplier: 1,
    building: [
      { level: 1, room: null },
      { level: 2, room: null },
      { level: 3, room: null },
    ],
    visitorGroup: null,
    reviews: [],
    isOpen: false,
    ticketPrice: 10,
    dayNumber: 1,
    tutorialStep: 0,
    dayStartTime: 0,
    todayVisitors: 0,
    todayEarnings: 0,
    reputation: 50,
    recommendationPool: 0,
    todayFleeCount: 0,
    lobbyQueue: [],
    activeTeams: [],
    maxConcurrentTeams: 2,
    lobbyStaff: [],
    lobbyWaitQueue: [],
    maxQueueSize: 10,
    lobbyFacilities: [
      {
        id: 'init-facility-ticket',
        type: 'ticket_booth' as LobbyFacilityType,
        level: 1,
        assignedStaffId: null,
      },
    ],
    queueTickAccumMs: 0,
    queueSpawnAccumMs: 0,
    lobbyBuilding: [
      {
        level: 1,
        facilityType: 'ticket_booth' as LobbyFacilityType,
        facilityLevel: 1,
        assignedGhostId: null,
        props: [],
      },
      {
        level: 2,
        facilityType: null,
        facilityLevel: 0,
        assignedGhostId: null,
        props: [],
      },
      {
        level: 3,
        facilityType: null,
        facilityLevel: 0,
        assignedGhostId: null,
        props: [],
      },
    ],
    facilityBuilding: [],
    facilityMap: Array.from({ length: 16 }, (_, i) => ({
      id: i,
      unlocked: i < 4,
      facility: i === 0 ? {
        type: 'haunted_house' as any,
        upgradeLevel: 1,
        assignedGhostId: null,
        props: [],
        price: 10,
        buildingId: 'building_1',
        totalRevenue: 0,
        isOpen: true,
        totalRatings: 0,
        ratingSum: 0,
        visitorCount: 0,
      } : i === 1 ? {
        type: 'ticket_booth' as any,
        upgradeLevel: 1,
        assignedGhostId: null,
        props: [],
        price: 0,
        totalRevenue: 0,
        isOpen: true,
        totalRatings: 0,
        ratingSum: 0,
        visitorCount: 0,
      } : null,
    })),
    storyBonus: 0,
    storyGrade: 'F',
    activeEvent: null,
    eventHistory: [],
    eventCooldownUntil: 0,
    eventTickAccumMs: 0,

  };
}

export function createInitialHauntedBuildingMetas(): HauntedBuildingMeta[] {
  const metas: HauntedBuildingMeta[] = [
    {
      id: 'building_1',
      name: '나의 첫 번째 귀신의 집',
      themeId: 'haunted_house',
      buildingExteriorAsset: '/images/views/building-exterior-haunted-house.webp',
      lobbyStyleId: 'classic_haunted_lobby',
      passiveRevenueMultiplier: 1,
      maxFloors: 5,
      isUnlocked: true,
    },
  ];
  // Slots 2-10: one for each remaining theme
  for (let i = 2; i <= 10; i++) {
    metas.push({
      id: `building_${i}`,
      name: '',
      themeId: null,
      buildingExteriorAsset: '',
      lobbyStyleId: '',
      passiveRevenueMultiplier: 0.7,
      maxFloors: 5,
      isUnlocked: false,
    });
  }
  return metas;
}

export function getSelectedHauntedBuilding(state: GameStateData): HauntedBuildingState {
  return state.hauntedBuildings.find((building) => building.id === state.selectedHauntedBuildingId)
    ?? state.hauntedBuildings[0]
    ?? createInitialHauntedBuildingState();
}

export function mapStateToSelectedBuilding(state: GameStateData): GameStateData {
  const selected = getSelectedHauntedBuilding(state);
  return {
    ...state,
    building: selected.building,
    visitorGroup: selected.visitorGroup,
    reviews: selected.reviews,
    isOpen: selected.isOpen,
    ticketPrice: selected.ticketPrice,
    dayNumber: selected.dayNumber,
    tutorialStep: selected.tutorialStep ?? 0,
    dayStartTime: selected.dayStartTime,
    todayVisitors: selected.todayVisitors,
    todayEarnings: selected.todayEarnings,
    reputation: selected.reputation,
    recommendationPool: selected.recommendationPool,
    todayFleeCount: selected.todayFleeCount,
    lobbyQueue: selected.lobbyQueue,
    activeTeams: selected.activeTeams,
    maxConcurrentTeams: selected.maxConcurrentTeams,
    lobbyStaff: selected.lobbyStaff,
    lobbyWaitQueue: selected.lobbyWaitQueue,
    maxQueueSize: selected.maxQueueSize,
    lobbyFacilities: selected.lobbyFacilities,
    queueTickAccumMs: selected.queueTickAccumMs,
    queueSpawnAccumMs: selected.queueSpawnAccumMs,
    lobbyBuilding: selected.lobbyBuilding,
    facilityBuilding: selected.facilityBuilding,
    facilityMap: selected.facilityMap,
    storyBonus: selected.storyBonus,
    storyGrade: selected.storyGrade,
    activeEvent: selected.activeEvent,
    eventHistory: selected.eventHistory,
    eventCooldownUntil: selected.eventCooldownUntil,
    eventTickAccumMs: selected.eventTickAccumMs,
  };
}

export function syncSelectedBuildingToState(state: GameStateData): GameStateData {
  const selectedId = state.selectedHauntedBuildingId;
  const nextBuildings = state.hauntedBuildings.map((building) => {
    if (building.id !== selectedId) return building;
    return {
      ...building,
      building: state.building,
      visitorGroup: state.visitorGroup,
      reviews: state.reviews,
      isOpen: state.isOpen,
      ticketPrice: state.ticketPrice,
      dayNumber: state.dayNumber,
      tutorialStep: state.tutorialStep,
      dayStartTime: state.dayStartTime,
      todayVisitors: state.todayVisitors,
      todayEarnings: state.todayEarnings,
      reputation: state.reputation,
      recommendationPool: state.recommendationPool,
      todayFleeCount: state.todayFleeCount,
      lobbyQueue: state.lobbyQueue,
      activeTeams: state.activeTeams,
      maxConcurrentTeams: state.maxConcurrentTeams,
      lobbyStaff: state.lobbyStaff,
      lobbyWaitQueue: state.lobbyWaitQueue,
      maxQueueSize: state.maxQueueSize,
      lobbyFacilities: state.lobbyFacilities,
      queueTickAccumMs: state.queueTickAccumMs,
      queueSpawnAccumMs: state.queueSpawnAccumMs,
      lobbyBuilding: state.lobbyBuilding,
      facilityBuilding: state.facilityBuilding,
      facilityMap: state.facilityMap,
      storyBonus: state.storyBonus,
      storyGrade: state.storyGrade,
      activeEvent: state.activeEvent,
      eventHistory: state.eventHistory,
      eventCooldownUntil: state.eventCooldownUntil,
      eventTickAccumMs: state.eventTickAccumMs,
    };
  });

  return {
    ...state,
    hauntedBuildings: nextBuildings,
  };
}

export function summarizeHauntedBuildings(state: GameStateData): HauntedBuildingSummary[] {
  return state.hauntedBuildingMetas.map((meta) => {
    const building = state.hauntedBuildings.find((item) => item.id === meta.id);
    return {
      id: meta.id,
      name: meta.name,
      themeId: meta.themeId,
      buildingExteriorAsset: meta.buildingExteriorAsset,
      lobbyStyleId: meta.lobbyStyleId,
      passiveRevenueMultiplier: meta.passiveRevenueMultiplier,
      maxFloors: meta.maxFloors,
      isUnlocked: meta.isUnlocked,
      floorsBuilt: building?.building.filter((floor) => floor.room !== null).length ?? 0,
      todayEarnings: building?.todayEarnings ?? 0,
      reputation: building?.reputation ?? 0,
      queueCount: building?.lobbyWaitQueue.length ?? 0,
      isOpen: building?.isOpen ?? false,
    };
  });
}

export function upsertHauntedBuildingMeta(state: GameStateData, partial: HauntedBuildingMeta): HauntedBuildingMeta[] {
  const existing = state.hauntedBuildingMetas.find((meta) => meta.id === partial.id);
  if (!existing) {
    return [...state.hauntedBuildingMetas, partial];
  }
  return state.hauntedBuildingMetas.map((meta) => (meta.id === partial.id ? partial : meta));
}

export function cloneBuildingStateFromCurrent(
  _state: GameStateData,
  meta: HauntedBuildingMeta,
): HauntedBuildingState {
  const base = createInitialHauntedBuildingState();
  return {
    ...base,
    id: meta.id,
    name: meta.name,
    themeId: meta.themeId,
    maxFloors: meta.maxFloors,
    buildingExteriorAsset: meta.buildingExteriorAsset,
    lobbyStyleId: meta.lobbyStyleId,
    passiveRevenueMultiplier: meta.passiveRevenueMultiplier,
  };
}

export function getHauntedBuildingMeta(state: GameStateData, buildingId: string): HauntedBuildingMeta | undefined {
  return state.hauntedBuildingMetas.find((meta) => meta.id === buildingId);
}

export function isHauntedBuildingUnlocked(state: GameStateData, buildingId: string): boolean {
  return getHauntedBuildingMeta(state, buildingId)?.isUnlocked ?? false;
}

export function unlockHauntedBuildingMeta(state: GameStateData, buildingId: string): HauntedBuildingMeta[] {
  return state.hauntedBuildingMetas.map((meta) => (
    meta.id === buildingId ? { ...meta, isUnlocked: true } : meta
  ));
}

export function applyHauntedBuildingSelection(state: GameStateData, buildingId: string): GameStateData {
  const nextId = isHauntedBuildingUnlocked(state, buildingId) ? buildingId : state.selectedHauntedBuildingId;
  return mapStateToSelectedBuilding({
    ...syncSelectedBuildingToState(state),
    selectedHauntedBuildingId: nextId,
  });
}

export function applyHauntedBuildingPassiveTick(state: GameStateData): GameStateData {
  const selectedId = state.selectedHauntedBuildingId;
  const nextBuildings = state.hauntedBuildings.map((building) => {
    if (building.id === selectedId || !building.isOpen) return building;
    const passiveGain = Math.max(0, Math.round((building.todayEarnings * 0.01) * building.passiveRevenueMultiplier));
    return {
      ...building,
      todayEarnings: building.todayEarnings + passiveGain,
    };
  });
  return { ...state, hauntedBuildings: nextBuildings };
}

export function withSelectedHauntedBuilding(
  state: GameStateData,
  updater: (selected: HauntedBuildingState) => HauntedBuildingState,
): GameStateData {
  const synced = syncSelectedBuildingToState(state);
  const nextBuildings = synced.hauntedBuildings.map((building) => (
    building.id === synced.selectedHauntedBuildingId ? updater(building) : building
  ));
  return mapStateToSelectedBuilding({
    ...synced,
    hauntedBuildings: nextBuildings,
  });
}

export function getMaxFloorsForSelectedBuilding(state: GameStateData): number {
  return getSelectedHauntedBuilding(state).maxFloors;
}

export function getSelectedBuildingThemeId(state: GameStateData): HauntedThemeSelection {
  return getSelectedHauntedBuilding(state).themeId;
}

export function getSelectedBuildingName(state: GameStateData): string {
  return getSelectedHauntedBuilding(state).name || '새 건물';
}

export function getEmptyRoomImage(state: GameStateData): string {
  const themeId = getSelectedBuildingThemeId(state);
  if (!themeId || themeId === 'haunted_house') return '/images/room/empty.webp';
  return `/images/room/empty-${themeId}.webp`;
}

export function getHauntedThemeDefaults(themeId: HauntedThemeId): Pick<HauntedBuildingMeta, 'buildingExteriorAsset' | 'lobbyStyleId'> {
  switch (themeId) {
    case 'abandoned_hospital':
      return {
        buildingExteriorAsset: '/images/views/building-exterior-abandoned-hospital.webp',
        lobbyStyleId: 'hospital_lobby',
      };
    case 'school_back_mountain':
      return {
        buildingExteriorAsset: '/images/views/building-exterior-school-back-mountain.webp',
        lobbyStyleId: 'school_lobby',
      };
    case 'haunted_house':
    default:
      return {
        buildingExteriorAsset: '/images/views/building-exterior-haunted-house.webp',
        lobbyStyleId: 'classic_haunted_lobby',
      };
  }
}

export function configureHauntedBuildingMeta(
  state: GameStateData,
  buildingId: string,
  name: string,
  themeId: HauntedThemeId,
): HauntedBuildingMeta[] {
  const defaults = getHauntedThemeDefaults(themeId);
  return state.hauntedBuildingMetas.map((meta) => (
    meta.id === buildingId
      ? {
          ...meta,
          name,
          themeId,
          buildingExteriorAsset: defaults.buildingExteriorAsset,
          lobbyStyleId: defaults.lobbyStyleId,
        }
      : meta
  ));
}

export function getSelectedBuildingMeta(state: GameStateData): HauntedBuildingMeta | undefined {
  return getHauntedBuildingMeta(state, state.selectedHauntedBuildingId);
}

export function syncSelectedMetaFromBuilding(state: GameStateData): GameStateData {
  const selected = getSelectedHauntedBuilding(state);
  return {
    ...state,
    hauntedBuildingMetas: state.hauntedBuildingMetas.map((meta) => (
      meta.id === selected.id
        ? {
            ...meta,
            name: selected.name,
            themeId: selected.themeId,
            buildingExteriorAsset: selected.buildingExteriorAsset,
            lobbyStyleId: selected.lobbyStyleId,
            passiveRevenueMultiplier: selected.passiveRevenueMultiplier,
            maxFloors: selected.maxFloors,
          }
        : meta
    )),
  };
}

export function createInitialState(): GameStateData {
  const initialBuilding = createInitialHauntedBuildingState();
  return {
    sp: 0,
    fame: 0,
    prestigeCoins: 0,
    prestigeLevel: 0,
    totalSpEarned: 0,
    selectedHauntedBuildingId: initialBuilding.id,
    worldScreen: 'world',
    hauntedBuildings: [initialBuilding],
    hauntedBuildingMetas: createInitialHauntedBuildingMetas(),
    building: initialBuilding.building,
    ghosts: [
      {
        id: 'init-ghost-1',
        characterId: 'jinaeryeong' as GhostId,
        level: 1,
        experience: 0,
        fatigue: 0,
        assignedRoomId: null,
        training: null,
        trainingBonus: 0,
        trainingCount: 0,
        role: 'scare' as const,
        assignedFacilityId: null,
        academySession: null,
        academySkills: { ...INITIAL_ACADEMY_SKILLS },
        hiredAt: 0,
        praiseCount: 0,
      },
      {
        id: 'init-ghost-2',
        characterId: 'gangsi' as GhostId,
        level: 1,
        experience: 0,
        fatigue: 0,
        assignedRoomId: null,
        training: null,
        trainingBonus: 0,
        trainingCount: 0,
        role: 'scare' as const,
        assignedFacilityId: null,
        academySession: null,
        academySkills: { ...INITIAL_ACADEMY_SKILLS },
        hiredAt: 0,
        praiseCount: 0,
      },
      {
        id: 'init-ghost-3',
        characterId: 'pierrot' as GhostId,
        level: 1,
        experience: 0,
        fatigue: 0,
        assignedRoomId: null,
        training: null,
        trainingBonus: 0,
        trainingCount: 0,
        role: 'scare' as const,
        assignedFacilityId: null,
        academySession: null,
        academySkills: { ...INITIAL_ACADEMY_SKILLS },
        hiredAt: 0,
        praiseCount: 0,
      },
    ],
    visitorGroup: initialBuilding.visitorGroup,
    nextVisitorSpawnAt: 0,
    fatigueTickAccumMs: 0,
    scarePopups: [],
    lastSaveTime: 0,
    unlockedRoomTypes: ['dark_corridor', 'mirror_room'] as RoomTypeId[],
    reviews: initialBuilding.reviews,
    facilities: [],
    adBoosts: [],
    starLevel: 0,
    starEvolution: 0,
    isOpen: initialBuilding.isOpen,
    ticketPrice: initialBuilding.ticketPrice,
    dayNumber: initialBuilding.dayNumber,
    tutorialStep: initialBuilding.tutorialStep ?? 0,
    dayStartTime: initialBuilding.dayStartTime,
    todayVisitors: initialBuilding.todayVisitors,
    todayEarnings: initialBuilding.todayEarnings,
    reputation: initialBuilding.reputation,
    timePhase: 'day' as const,
    timePhaseStartAt: 0,
    recommendationPool: initialBuilding.recommendationPool,
    todayFleeCount: initialBuilding.todayFleeCount,
    currentView: 'building' as MainView,
    lobbyQueue: initialBuilding.lobbyQueue,
    activeTeams: initialBuilding.activeTeams,
    maxConcurrentTeams: initialBuilding.maxConcurrentTeams,
    lobbyStaff: initialBuilding.lobbyStaff,
    lobbyWaitQueue: initialBuilding.lobbyWaitQueue,
    maxQueueSize: initialBuilding.maxQueueSize,
    lobbyFacilities: initialBuilding.lobbyFacilities,
    queueTickAccumMs: initialBuilding.queueTickAccumMs,
    queueSpawnAccumMs: initialBuilding.queueSpawnAccumMs,
    lobbyBuilding: initialBuilding.lobbyBuilding,
    facilityBuilding: initialBuilding.facilityBuilding,
    facilityMap: initialBuilding.facilityMap,
    storyBonus: initialBuilding.storyBonus,
    storyGrade: initialBuilding.storyGrade,
    dailyMilestones: [],
    dayPhase: 'day' as DayPhase,
    dayPhaseStartedAt: 0,
    gameSpeed: 1 as const,
    ghostStaff: [
      { id: 'init-staff-1', type: 'repairman' as GhostStaffType, name: 'Alex', level: 1, salary: 50, assignedTo: null, hiredAt: 0 },
      { id: 'init-staff-2', type: 'janitor' as GhostStaffType, name: 'Sam', level: 1, salary: 30, assignedTo: null, hiredAt: 0 },
    ],
    todaySalaries: 0,
    lobbyNpcGhosts: [],
    activeEvent: initialBuilding.activeEvent,
    eventHistory: initialBuilding.eventHistory,
    eventCooldownUntil: initialBuilding.eventCooldownUntil,
    eventTickAccumMs: initialBuilding.eventTickAccumMs,
    streetVisitors: [],
    streetSpawnAccumMs: 0,
    maxStreetVisitors: 20,
    streetAiAccumMs: 0,
    wordOfMouth: 1.0,
    seasonEvent: null,
    seasonEventStartedAt: 0,
  };
}

// ── Initial State ──────────────────────────────────────────

export const INITIAL_STATE: GameStateData = createInitialState();
